import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import { useMerchantShopsHydrate } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { ShopSwitcher } from "@/modules/merchant/presentation/ui/ShopSwitcher";
import { getCookie } from "@/shared/lib/cookies";
import { SkipToMain } from "@/shared/ui/SkipToMain";
import {
  cn,
  Loader,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
} from "@devhop/ui";
import { Link, Outlet, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect } from "react";
import { LayoutProvider } from "../providers/LayoutProvider";
import { AppSidebar } from "./AppSidebar";
import { MerchantMobileBottomNav } from "./MerchantMobileBottomNav";
import { MerchantNavGroup } from "./MerchantNavGroup";
import { NavUser } from "./NavUser";
import { ShopVerificationSidebarAlert } from "./ShopVerificationSidebarAlert";

type AuthenticatedLayoutProps = {
  children?: React.ReactNode;
};

export function AuthenticatedLayout({ children }: AuthenticatedLayoutProps) {
  const defaultOpen = getCookie("sidebar_state") !== "false";
  const { isLoading, isAuthenticated } = useAuthState();
  const navigate = useNavigate({ from: "/app" });
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;

  useMerchantShopsHydrate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate({ to: "/auth/login" });
    }
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <div className="container grid h-svh max-w-none items-center justify-center">
        <Loader />
      </div>
    );
  }
  return (
    <SidebarProvider defaultOpen={defaultOpen}>
      <LayoutProvider>
        <SkipToMain />
        <AppSidebar>
          <SidebarHeader>
            <ShopSwitcher />
          </SidebarHeader>
          <SidebarContent>
            {!shopId && (
              <SidebarGroup>
                <SidebarGroupLabel>ທົ່ວໄປ</SidebarGroupLabel>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild>
                      <Link to="/app">ລາຍການລາຍ</Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroup>
            )}
            <MerchantNavGroup />
          </SidebarContent>
          <SidebarFooter>
            <ShopVerificationSidebarAlert />
            <NavUser />
          </SidebarFooter>
          <SidebarRail />
        </AppSidebar>
        <SidebarInset
          className={cn(
            // If layout is fixed, set the height
            // to 100svh to prevent overflow
            "has-[[data-layout=fixed]]:h-svh",

            // If layout is fixed and sidebar is inset,
            // set the height to 100svh - 1rem (total margins) to prevent overflow
            // 'peer-data-[variant=inset]:has-[[data-layout=fixed]]:h-[calc(100svh-1rem)]',
            "peer-data-[variant=inset]:has-[[data-layout=fixed]]:h-[calc(100svh-(var(--spacing)*4))]",

            // Set content container, so we can use container queries
            "@container/content",
            shopId && "pb-[calc(4.5rem+env(safe-area-inset-bottom))] md:pb-0",
          )}
        >
          {children ?? <Outlet />}
          {shopId ? <MerchantMobileBottomNav /> : null}
        </SidebarInset>
      </LayoutProvider>
    </SidebarProvider>
  );
}
